import React, { useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { getIconDefinition } from '@/utils/iconUtils';
import { faXmark } from '@fortawesome/pro-duotone-svg-icons';

interface ModalProps {
  isOpen: boolean;
  onClose?: () => void;
  icon?: string;
  title?: string;
  children?: React.ReactNode;
  actions?: React.ReactNode;
  backgroundColor?: string;
  contentRef?: React.RefObject<HTMLDivElement | null>;
  closeOnClickOutside?: boolean;
}

const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  icon,
  title,
  children,
  actions,
  backgroundColor,
  contentRef,
  closeOnClickOutside = true,
}) => {
  const isMouseMoving = useRef(false);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (onClose) {
          onClose();
        }
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  const handleMouseDown = () => {
    isMouseMoving.current = false;
  };

  const handleMouseMove = () => {
    isMouseMoving.current = true;
  };

  const handleClick = () => {
    if (!isMouseMoving.current && closeOnClickOutside) {
      if (onClose) {
        onClose();
      }
    }
  };

  return (
    <div
      className={`modal ${isOpen ? 'modal-open' : ''}`}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onClick={handleClick}
    >
      <div
        className={`modal-box relative flex max-h-[90vh] w-fit max-w-[90vw] flex-col overflow-y-hidden ${backgroundColor}`}
        onClick={(e) => e.stopPropagation()}
      >
        {onClose && (
          <button onClick={onClose} className="btn btn-circle btn-ghost btn-sm absolute right-4 top-5">
            <FontAwesomeIcon icon={faXmark} className="h-4 w-4" />
          </button>
        )}
        {(icon || title) && (
          <div className="flex items-center px-1">
            {icon && (
              <div className="mr-2 flex items-center">
                <FontAwesomeIcon icon={getIconDefinition(icon)} className="fa-fw h-6 w-6" />
              </div>
            )}
            {title && <div className="text-lg font-bold">{title}</div>}
          </div>
        )}
        {children && (
          <div className="mt-4 flex-grow overflow-y-auto px-1" ref={contentRef}>
            {children}
          </div>
        )}
        {actions && <div className="modal-action">{actions}</div>}
      </div>
    </div>
  );
};

export default Modal;
